import type { FaqEntry } from '../faq';

/**
 * Catalan FAQ, translated from `es.ts`, which is the source.
 *
 * Product names follow the catalogs: `Redondeo Solidario` is "Arrodoniment Solidari" and
 * `entidades sociales` is "entitats socials". Informal second person, as the Spanish uses `tú`.
 */
export const faqEntriesCa: FaqEntry[] = [
	// MARK: 1. Per a empreses
	{
		block: 'companies',
		question: 'Què és Worldcoo?',
		answer:
			'Worldcoo és una empresa tecnològica que connecta empreses, persones i entitats socials per facilitar noves formes de donació i generar impacte social i mediambiental. Des del 2012 desenvolupem solucions que permeten integrar la donació en els canals de compra de les empreses i convertir petits gestos quotidians en oportunitats d’impacte.',
	},
	{
		block: 'companies',
		question: 'Quines solucions ofereix Worldcoo?',
		answer:
			'El nostre objectiu és facilitar la integració de la solidaritat en els diferents punts de contacte entre les empreses i els seus clients o empleats. Worldcoo desenvolupa diferents solucions de donació que s’adapten als distints canals i necessitats de cada empresa. Entre elles hi ha l’Arrodoniment Solidari, les donacions en ecommerce, els portals de donació i les solucions de donació vinculades a nòmines o factures.',
	},
	{
		block: 'companies',
		question: 'Què és l’Arrodoniment Solidari?',
		answer:
			'L’Arrodoniment Solidari permet als clients d’una empresa fer una petita donació en finalitzar una compra. El client pot arrodonir l’import de la seva compra i donar la diferència a un projecte social o mediambiental seleccionat.',
	},
	{
		block: 'companies',
		question: 'Quin cost té incorporar Worldcoo?',
		answer:
			'Analitzem cada cas de manera personalitzada, tenint en compte les necessitats de l’empresa, la solució que es vulgui implementar i el tipus d’integració tecnològica requerida. En funció de l’operativa i la implementació necessàries, la incorporació de les solucions de Worldcoo pot ser gratuïta.',
	},
	{
		block: 'companies',
		question: 'He de canviar el meu sistema de pagament per incorporar Worldcoo?',
		answer:
			'Worldcoo treballa amb diferents proveïdors tecnològics i sistemes de pagament per integrar les seves solucions en els canals de cada empresa. El nostre equip analitza cada cas per identificar la millor opció d’integració.',
	},
	{
		block: 'companies',
		question: 'En quins canals puc activar Worldcoo?',
		answer:
			'Les solucions de Worldcoo es poden integrar en diferents punts de contacte amb el client, tant físics com digitals, i també amb empleats:',
		bullets: [
			'Botigues i establiments físics.',
			'Ecommerce.',
			'Apps.',
			'Portals de Donació en línia',
			'Nòmines i factures',
		],
		outro: 'La solució s’adapta a les característiques i necessitats de cada empresa.',
	},
	{
		block: 'companies',
		question: 'Puc triar quant dona el client?',
		answer:
			'Sí. Treballem amb cada empresa per identificar projectes socials o mediambientals alineats amb els seus valors, objectius i prioritats. Worldcoo proposa una selecció de projectes que poden encaixar amb cada companyia, però la decisió final sobre quina causa donar suport correspon sempre a l’empresa.',
	},
	{
		block: 'companies',
		question: 'Puc triar quina causa o projecte vull donar suport?',
		answer:
			'Sí. Worldcoo treballa amb les empreses per identificar projectes alineats amb els seus valors, objectius d’impacte i prioritats socials o mediambientals. Worldcoo presenta una proposta de projectes que creu que poden encaixar amb cada companyia en concret, però és l’empresa qui té l’última paraula sobre el projecte triat.',
	},
	{
		block: 'companies',
		question: 'Puc donar suport a projectes locals?',
		answer:
			'Sí. A Worldcoo treballem amb entitats socials de diferents mides i àmbits d’actuació, incloses organitzacions locals que desenvolupen projectes amb un gran impacte en les seves comunitats. Un dels nostres objectius és facilitar també l’accés a finançament a aquestes entitats i donar visibilitat a la important tasca que fan.',
	},
	{
		block: 'companies',
		question: 'Puc canviar de projecte?',
		answer:
			'Sí. Les campanyes es poden actualitzar quan un projecte assoleix el seu objectiu de finançament, finalitza o quan l’empresa decideix donar suport a una nova causa.',
	},
	{
		block: 'companies',
		question: 'Puc saber quant s’ha recaptat?',
		answer:
			'Sí. Worldcoo ofereix eines de seguiment que permeten conèixer l’evolució de les campanyes i l’impacte generat de manera diària i segmentada per establiments.',
	},
	{
		block: 'companies',
		question: 'Puc comunicar els resultats de la campanya?',
		answer:
			'Sí. Worldcoo proporciona informació sobre l’evolució de les campanyes i els projectes finançats per facilitar la comunicació de l’impacte generat.',
	},

	// MARK: 2. Per a entitats socials
	{
		block: 'ngos',
		question: 'Com pot la meva entitat recaptar fons a través de Worldcoo?',
		answer:
			'Les entitats socials poden presentar els seus projectes a Worldcoo per optar a formar part de les campanyes de recaptació que s’activen a través d’empreses col·laboradores. El nostre equip analitza cada proposta i, quan hi ha una oportunitat de col·laboració, treballa amb l’entitat per posar en marxa la campanya.',
	},
	{
		block: 'ngos',
		question: 'Quin tipus de projectes finança Worldcoo?',
		answer: 'Treballem amb projectes de diferents àmbits socials i mediambientals, entre ells:',
		bullets: [
			'Infància i joventut.',
			'Inclusió social.',
			'Salut.',
			'Gent gran.',
			'Discapacitat.',
			'Medi ambient.',
			'Cooperació.',
			'Emergències.',
			'Altres àmbits d’impacte social.',
		],
	},
	{
		block: 'ngos',
		question: 'Quins requisits ha de complir una entitat per col·laborar amb Worldcoo?',
		answer:
			'Les entitats han de complir els criteris de selecció establerts per Worldcoo i aportar la documentació necessària per acreditar la seva constitució, activitat i situació legal. A més, han de tenir capacitat per emetre els certificats de donació corresponents.',
		outro: 'Pots consultar els criteris de selecció de Worldcoo per conèixer tots els requisits.',
		link: { label: 'Criteris de selecció', target: 'selection-criteria' },
	},
	{
		block: 'ngos',
		question: 'Com començar a col·laborar?',
		answer: 'Pots iniciar el procés omplint el formulari d’alta amb la informació bàsica de la teva entitat.',
		link: { label: 'Anar al formulari d’alta', target: 'ngo-signup' },
	},
	{
		block: 'ngos',
		question: 'Puc presentar un projecte concret?',
		answer:
			'Sí. Worldcoo ajuda a finançar projectes concrets de les entitats socials, no a finançar les entitats de manera general. Pots compartir amb nosaltres un o diversos projectes que necessitin finançament. El nostre equip en valorarà l’adequació als criteris de col·laboració i buscarà les oportunitats que millor puguin encaixar amb cada projecte.',
	},
	{
		block: 'ngos',
		question: 'Poden col·laborar entitats petites o locals?',
		answer:
			'Sí. A Worldcoo volem contribuir que també les entitats locals i de menor mida puguin accedir a noves oportunitats de finançament. Moltes d’aquestes organitzacions fan una tasca essencial a les seves comunitats i generen un impacte directe molt rellevant.',
	},
	{
		block: 'ngos',
		question: 'Com funciona el procés d’elecció de projectes?',
		answer:
			'Un cop formes part de la comunitat de Worldcoo, els teus projectes passen a formar part de la nostra base de dades. Quan sorgeix una oportunitat de col·laboració, presentem els projectes que millor poden encaixar amb les necessitats i criteris de cada empresa, tenint en compte aspectes com l’àmbit territorial i temàtic, la seva estratègia de RSC o el perfil dels seus clients.',
		outro:
			'La decisió final sobre quina organització col·laborarà en cada campanya correspon sempre a l’empresa col·laboradora.',
	},
	{
		block: 'ngos',
		question: 'Un cop finançat un projecte, com es gestionen els fons recaptats?',
		answer:
			'Un cop finalitza la campanya de recaptació, Worldcoo gestiona la transferència del 100% dels fons a l’entitat social beneficiària. El procés es fa en dos pagaments:',
		bullets: [
			'Un primer pagament del 50 % de l’import recaptat en finalitzar la campanya.',
			'Un segon pagament del 50 % restant un cop l’entitat presenti l’informe de seguiment i justificació del projecte.',
		],
	},
	{
		block: 'ngos',
		question: 'Quins compromisos adquireix una entitat quan rep finançament?',
		answer:
			'Les entitats que reben fons a través de Worldcoo es comprometen a informar sobre l’execució i l’impacte del projecte finançat. Per fer-ho, han de presentar un informe que inclogui informació sobre:',
		bullets: [
			'El desenvolupament del projecte i les persones beneficiàries.',
			'Els resultats i l’impacte aconseguit.',
			'Imatges o altra documentació que permeti conèixer l’evolució del projecte.',
			'L’ús i destí dels fons rebuts.',
		],
	},
	{
		block: 'ngos',
		question: 'Quin cost té col·laborar amb Worldcoo per a una entitat social?',
		answer:
			'Worldcoo ofereix a les entitats socials serveis de captació, gestió i seguiment de fons, així com accés a noves oportunitats de col·laboració amb empreses. Per aquests serveis, Worldcoo aplica una tarifa sobre els fons gestionats. Les condicions es comuniquen de manera transparent a cada entitat abans de l’inici de qualsevol col·laboració.',
	},

	// MARK: 3. Per a persones donants
	{
		block: 'donors',
		question: 'Què és l’Arrodoniment Solidari?',
		answer:
			'L’Arrodoniment Solidari és una manera senzilla de fer una petita donació en fer una compra. En pagar, pots arrodonir l’import de la teva compra i destinar aquests cèntims a un projecte social o mediambiental.',
	},
	{
		block: 'donors',
		question: 'On puc fer una donació?',
		answer:
			'Pots trobar solucions de donació de Worldcoo en diferents establiments i canals digitals de les nostres empreses col·laboradores.',
	},
	{
		block: 'donors',
		question: 'Quants diners dono?',
		answer:
			'L’import de la donació depèn de la solució i de la campanya en què participis. En el cas de l’Arrodoniment Solidari, pots arrodonir l’import de la teva compra i donar la diferència. En altres campanyes, pots fer una donació d’un import fix establert prèviament. Abans de confirmar la teva donació, sempre podràs conèixer l’import que hi aportaràs.',
	},
	{
		block: 'donors',
		question: 'Puc decidir si vull donar?',
		answer:
			'Sí. La participació en l’Arrodoniment Solidari és voluntària i anònima. Abans de completar la donació, el sistema t’informa de l’import que hi pots aportar.',
	},
	{
		block: 'donors',
		question: 'A quin projecte es destina la meva donació?',
		answer:
			'La teva donació es destina al projecte associat a la campanya en què estàs participant. Pots consultar informació sobre el projecte, l’entitat responsable i l’evolució de la campanya a través dels canals habilitats per Worldcoo i per cada empresa.',
	},
	{
		block: 'donors',
		question: 'Com puc saber quant s’ha recaptat?',
		answer:
			'Worldcoo ofereix informació sobre els objectius de cada campanya i la seva evolució per facilitar el seguiment de les donacions i de l’impacte generat.',
	},
	{
		block: 'donors',
		question: 'Què passa amb els meus diners després de fer una donació?',
		answer:
			'Un cop feta la donació, Worldcoo gestiona els fons recaptats durant la campanya i en fa el seguiment. En finalitzar la campanya, els fons es destinen a l’entitat social responsable del projecte per contribuir al seu finançament. Posteriorment, l’entitat informa sobre el desenvolupament i l’impacte aconseguit gràcies als fons recaptats.',
	},
	{
		block: 'donors',
		question: 'Puc obtenir un certificat de la meva donació?',
		answer:
			'Sí. Pots sol·licitar el teu certificat de donació a través d’app.worldcoo.com. Per fer-ho, necessitaràs conservar el tiquet o comprovant de la teva donació, on trobaràs el codi de donació necessari per identificar-la.',
	},
	{
		block: 'donors',
		question: 'Què és el codi de donació?',
		answer:
			'El codi de donació és un identificador únic associat a la teva aportació. El pots trobar al tiquet o comprovant de donació i utilitzar-lo per registrar la teva donació a app.worldcoo.com i sol·licitar, quan correspongui, el teu certificat de donació.',
	},
	{
		block: 'donors',
		question: 'Com puc conèixer totes les donacions que he fet?',
		answer:
			'A través d’app.worldcoo.com pots registrar i consultar les donacions que has fet. La plataforma et permet tenir un històric de les teves donacions i sol·licitar els certificats corresponents.',
	},
	{
		block: 'donors',
		question: 'Puc cancel·lar o sol·licitar la devolució d’una donació?',
		answer:
			'Sí. Pots sol·licitar la devolució d’una donació. Els diners donats els gestiona directament Worldcoo, així que Worldcoo s’ocuparà de fer la devolució de l’import donat a petició del client.',
		outro: 'Entra al nostre apartat de contacte i escriu-nos amb el teu cas.',
		link: { label: 'Contacta amb nosaltres', target: 'contact' },
	},

	// MARK: 4. Transparència i seguretat
	{
		block: 'transparency',
		question: 'Com garanteix Worldcoo la transparència de les donacions?',
		answer:
			'La transparència és un dels pilars del nostre model. Worldcoo fa el seguiment de les campanyes i ofereix informació sobre les quantitats recaptades, els projectes finançats i les entitats participants. A més, publiquem periòdicament informació sobre la nostra activitat i l’impacte generat.',
	},
	{
		block: 'transparency',
		question: 'Quants diners arriben a les entitats socials?',
		answer:
			'El 100 % de les donacions recaptades en cada campanya es destina al projecte i a l’entitat social responsable de dur-lo a terme. Posteriorment, l’ONG s’encarrega de fer un pagament a Worldcoo pels serveis prestats.',
	},
	{
		block: 'transparency',
		question: 'Quants projectes ha finançat Worldcoo?',
		answer:
			'Des de la nostra creació, Worldcoo ha contribuït a finançar més de 1.000 projectes socials i mediambientals, en col·laboració amb centenars d’entitats.',
	},
	{
		block: 'transparency',
		question: 'Quantes donacions s’han fet a través de Worldcoo?',
		answer:
			'Milions de persones han participat en campanyes de Worldcoo i s’han fet més de 100 milions de microdonacions.',
	},
	{
		block: 'transparency',
		question: 'On puc consultar l’impacte de Worldcoo?',
		answer:
			'Pots consultar informació sobre les campanyes, projectes i impacte generat per Worldcoo a través del nostre web. També publiquem periòdicament informació sobre la nostra activitat i la nostra memòria d’impacte.',
	},
	{
		block: 'transparency',
		question: 'És segur donar a través de Worldcoo?',
		answer:
			'Sí. Les solucions de Worldcoo estan integrades en els sistemes de pagament de les nostres empreses col·laboradores i es desenvolupen seguint els estàndards de seguretat aplicables. A més, Worldcoo fa un seguiment de les campanyes i dels fons recaptats per garantir una gestió transparent de les donacions.',
	},
	{
		block: 'transparency',
		question: 'Worldcoo guarda les dades de la meva targeta?',
		answer: 'Worldcoo no necessita emmagatzemar les dades completes de la teva targeta perquè puguis fer una donació.',
	},
	{
		block: 'transparency',
		question: 'Com s’utilitzen les meves dades personals?',
		answer:
			'Les dades personals es tracten d’acord amb la nostra Política de Privacitat i amb la normativa vigent en matèria de protecció de dades.',
	},

	// MARK: 5. Sobre Worldcoo
	{
		block: 'about',
		question: 'Quan va néixer Worldcoo?',
		answer:
			'Worldcoo va néixer el 2012 amb l’objectiu d’utilitzar la tecnologia per facilitar el finançament de projectes socials i mediambientals.',
	},
	{
		block: 'about',
		question: 'On opera Worldcoo?',
		answer:
			'Worldcoo treballa amb empreses i entitats socials en diferents mercats i canalitza fons cap a projectes tant nacionals com internacionals.',
	},
	{
		block: 'about',
		question: 'Worldcoo és una ONG?',
		answer:
			'No. Worldcoo és una empresa tecnològica especialitzada en solucions de microdonació. El nostre objectiu és connectar empreses, persones i entitats socials per facilitar la col·laboració i generar impacte.',
	},
	{
		block: 'about',
		question: 'Worldcoo és una empresa B Corp?',
		answer:
			'Sí. Worldcoo compta amb la certificació B Corp, que reconeix el compromís de les empreses amb elevats estàndards d’impacte social i mediambiental, transparència i responsabilitat.',
	},
	{
		block: 'about',
		question: 'Per què Worldcoo?',
		answer:
			'Perquè creiem que donar hauria de ser fàcil. A Worldcoo utilitzem la tecnologia per connectar empreses, persones i entitats socials i convertir milions de petits gestos quotidians en oportunitats d’impacte. Des del 2012 treballem per fer que la col·laboració social sigui cada cop més senzilla, accessible i transparent.',
	},
];
